import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import type { ApiResponse } from '@/types/api';
import { USE_MOCKS } from '@/lib/mockConfig';
import { seedProducts, seedFaqs, normalizeProduct, type Product, type FaqEntry } from '../mockKnowledge';

/**
 * Knowledge base persistence:
 *  - Products live in the shop config's productsJson blob — GET /chatbot/products
 *    to load, PUT to save the whole list (the page debounces the save).
 *  - FAQs are first-class rows — GET /faqs + per-item POST/PATCH/DELETE.
 * Everything falls back to the seed data on an empty DB / unauthenticated.
 */
export function useKnowledge() {
  const productsQuery = useQuery({
    queryKey: ['knowledge', 'products'],
    queryFn: () =>
      apiClient.get<ApiResponse<Product[]>>('/api/chatbot/products')
        .then((res) => {
          const list = res.data.data || [];
          if (USE_MOCKS && list.length === 0) return seedProducts;
          // productsJson is a free-form blob — normalize each entry so missing
          // fields (source/title/stock/id) don't crash the table.
          return list.map((p, i) => normalizeProduct(p, i));
        })
        .catch((err) => {
          // ⚠️ Never seed a real tenant: the page auto-saves what it loaded, so sample products
          // shown after a failed request would be written into their real catalogue.
          if (USE_MOCKS) return seedProducts;
          throw err;
        }),
    staleTime: 60 * 1000,
  });

  const faqsQuery = useQuery({
    queryKey: ['knowledge', 'faqs'],
    queryFn: () =>
      apiClient.get<ApiResponse<FaqEntry[]>>('/api/faqs')
        .then((res) => {
          const list = res.data.data || [];
          if (USE_MOCKS && list.length === 0) return seedFaqs;
          return list;
        })
        .catch((err) => {
          if (USE_MOCKS) return seedFaqs;
          throw err;
        }),
    staleTime: 60 * 1000,
  });

  const persistProducts = useMutation({
    mutationFn: (products: Product[]) => apiClient.put('/api/chatbot/products', { productsJson: products }),
    onError: () => {},
  });

  // Business Knowledge: free-text shop documents (about, delivery, payment,
  // returns, policies). The AI answers from these + product docs via RAG.
  const businessKnowledgeQuery = useQuery({
    queryKey: ['knowledge', 'business-docs'],
    queryFn: () =>
      apiClient.get<ApiResponse<{ businessKnowledge: string }>>('/api/chatbot/knowledge-docs')
        .then((res) => res.data.data?.businessKnowledge ?? '')
        .catch(() => ''),
    staleTime: 60 * 1000,
  });

  const saveBusinessKnowledge = useMutation({
    mutationFn: (businessKnowledge: string) =>
      apiClient.put('/api/chatbot/knowledge-docs', { businessKnowledge }),
    onError: () => {},
  });

  const createFaq = useMutation({
    mutationFn: (entry: Pick<FaqEntry, 'question' | 'answer'>) =>
      apiClient.post<ApiResponse<FaqEntry>>('/api/faqs', entry).then((r) => r.data.data as FaqEntry),
    onError: () => {},
  });
  const updateFaq = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<FaqEntry> }) =>
      apiClient.patch(`/api/faqs/${id}`, patch),
    onError: () => {},
  });
  const deleteFaqMut = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/faqs/${id}`),
    onError: () => {},
  });

  return {
    loadedProducts: productsQuery.data ?? (USE_MOCKS ? seedProducts : []),
    loadedFaqs: faqsQuery.data ?? (USE_MOCKS ? seedFaqs : []),
    // The page must neither show nor auto-save a catalogue it failed to load (see KnowledgeBasePage).
    loadFailed: productsQuery.isError || faqsQuery.isError,
    businessKnowledge: businessKnowledgeQuery.data ?? '',
    isLoading: productsQuery.isLoading || faqsQuery.isLoading,
    persistProducts: persistProducts.mutate,
    saveBusinessKnowledge: saveBusinessKnowledge.mutateAsync,
    isSavingBusinessKnowledge: saveBusinessKnowledge.isPending,
    createFaq: createFaq.mutateAsync,
    updateFaq: updateFaq.mutate,
    deleteFaq: deleteFaqMut.mutate,
  };
}
